import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import {
    generatePath,
    useHistory,
    useParams,
    useRouteMatch,
} from 'react-router-dom';
import moment, { Moment } from 'moment';

import {
    activateChannelProfile,
    fetchWidgetChannel,
} from 'flow/actions/channels-actions';
import {
    activateQueueProfile,
    activateReferProfile,
    fetchQueue,
    fetchReferProfiles,
} from 'flow/actions/pbx-actions';
import Loading from 'flow/components/ui-components/Loading';
import Page from 'flow/components/Setting/Page';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import RouteLink from 'flow/components/ui-components/RouteLink';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { PbxParams } from 'flow/types/Params';
import { Profile } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { ISO_8601_MS_RESOLUTION } from 'flow/utils';
import {
    addGeneralLogEntry,
    addSupervisorLogEntry,
} from 'flow/utils/browser-utils';
import {
    getStandardProfileDescriptionInUserLanguage,
    resolveDefaultDuration,
} from 'flow/utils/profile-utils';
import { twMerge } from 'tailwind-merge';
import PbxProfileSkeleton from 'flow/components/loaders/PbxProfileSkeleton';

const RoutePbxProfiles = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();
    const { entityKey } = useParams<PbxParams>();
    const { sortedProfiles: profiles } = useProfiles(entityKey);
    const match = useRouteMatch();
    const [entityType] = entityKey.split('-');
    const [isLoading, setIsLoading] = React.useState(null);

    const queueDescription = useAppSelector((state) =>
        entityType === 'queue'
            ? state.pbx.getIn(['queues', entityKey, 'description'])
            : undefined,
    );

    useEffect(() => {
        if (entityType === 'queue') {
            dispatch(fetchQueue(entityKey));
        } else if (entityType === 'refer') {
            dispatch(fetchReferProfiles(entityKey));
        } else if (entityType === 'channel') {
            dispatch(fetchWidgetChannel(entityKey, ['all', 'contact']));
        }
    }, [dispatch, entityKey]);

    const handleBack = () => {
        const entityMainPage: Record<string, string> = {
            channel: routes['pbx.channels.entityKey'],
            queue: routes['pbx.queues.entityKey'],
            refer: routes['pbx.refers.entityKey'],
        };

        if ([routes['pbx.overview']].includes(match.path)) {
            history.push(routes['pbx.overview']);
        } else if (entityMainPage[entityType]) {
            history.push(
                generatePath(entityMainPage[entityType], {
                    entityKey,
                }),
            );
        }
    };

    const getCalendarPage = () => {
        const calendarPage: Record<string, string> = {
            channel: routes['pbx.channels.entityKey.profiles.calendar'],
            extension: routes['extensions.entityKey.contact.calendar'],
            queue: routes['pbx.queues.entityKey.profiles.calendar'],
            refer: routes['pbx.refers.entityKey.profiles.calendar'],
        };

        return calendarPage[entityType]
            ? generatePath(calendarPage[entityType], { entityKey })
            : null;
    };

    const addLogEntry = (logString: string) => {
        if ([routes['pbx.overview']].includes(match.path)) {
            addSupervisorLogEntry(logString);
        } else {
            addGeneralLogEntry(logString);
        }
    };

    const handleDefaultDurationActivation = (profile: TypedMap<Profile>) => {
        const defaultDuration = profile.get('defaultDuration');
        const until = resolveDefaultDuration(defaultDuration, moment());

        activate(profile, until);
    };

    const handleClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        profile: TypedMap<Profile>,
    ) => {
        event.stopPropagation();
        openModal('setProfileUntil', {
            profile,
            onSubmit: activate,
        });
    };

    const activate = (profile: TypedMap<Profile>, until: Moment) => {
        addLogEntry('[Profile activator] Activate default profile clicked');

        const updatedProfileDTO = profile
            .update('active', () => true)
            .update('activeUntil', () =>
                until ? until.format(ISO_8601_MS_RESOLUTION) : null,
            );

        const activators: Record<string, Function> = {
            queue: activateQueueProfile,
            refer: activateReferProfile,
            channel: activateChannelProfile,
        };

        if (!activators[entityType]) {
            throw new Error('Not implemented');
        }

        setIsLoading(profile.get('plusId'));

        dispatch(
            activators[entityType](entityKey, updatedProfileDTO, () => {
                handleBack();
                setIsLoading(null);
            }),
        );

        addLogEntry(
            `[Profile activator] ${entityType} profile ${profile.get(
                'description',
            )} activated`,
        );
    };

    const header =
        [routes['pbx.overview']].includes(match.path) && queueDescription
            ? `${i18next.t('pbx.pbxmode')} - ${queueDescription}`
            : i18next.t('pbx.opening_hours');

    return (
        <Page header={header} onClickBack={handleBack}>
            {!profiles || profiles?.isEmpty() ? (
                <PbxProfileSkeleton />
            ) : (
                profiles.map((profile, index) => (
                    <div
                        key={profile.get('key')}
                        className="flex h-13 w-full cursor-pointer items-center justify-between gap-x-3 rounded-sm px-2 hover:bg-gray-100"
                        onClick={() => handleDefaultDurationActivation(profile)}
                    >
                        <p
                            className={twMerge(
                                'flex-1 truncate',
                                profile.get('active')
                                    ? 'font-medium'
                                    : 'font-normal',
                            )}
                        >
                            {getStandardProfileDescriptionInUserLanguage(
                                profile.get('description'),
                                i18next,
                            )}
                        </p>
                        {isLoading === index && (
                            <div className="size-8">
                                <Loading size={32} />
                            </div>
                        )}

                        {!profile.get('defaultProfile') ? (
                            <IconButton
                                aria-label={i18next.t('profile.set_end_time')}
                                onClick={(event) => handleClick(event, profile)}
                            >
                                <Icon iconKey="accessTime" />
                            </IconButton>
                        ) : null}
                    </div>
                ))
            )}

            <Divider />

            <RouteLink to={getCalendarPage()}>
                {i18next.t('pbx.scheduleprofile')}
            </RouteLink>
        </Page>
    );
};

export default RoutePbxProfiles;
